"use client";

import type { FieldSource } from "@atlas/computation";
import { useState } from "react";

interface SourceBadgeProps {
  source: FieldSource;
  derivation?: string;
  confidence?: string;
}

const SOURCE_CONFIG: Record<
  FieldSource,
  {
    label: string;
    className: string;
    icon: "check" | "calc" | "chart" | "default";
  }
> = {
  user_provided: {
    label: "User",
    className: "bg-green-50 text-green-700 ring-1 ring-inset ring-green-600/20",
    icon: "check",
  },
  derived: {
    label: "Estimated",
    className: "bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-600/20",
    icon: "calc",
  },
  archetype_default: {
    label: "Default",
    className:
      "bg-background text-secondary-foreground ring-1 ring-inset ring-gray-500/20",
    icon: "default",
  },
  benchmark: {
    label: "Benchmark",
    className:
      "bg-warning text-warning-muted ring-1 ring-inset ring-amber-600/20",
    icon: "chart",
  },
};

function BadgeIcon({ type }: { type: "check" | "calc" | "chart" | "default" }) {
  switch (type) {
    case "check":
      return (
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      );
    case "calc":
      return (
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      );
    case "chart":
      return (
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      );
    case "default":
      return (
        <svg
          className="w-3 h-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      );
  }
}

/**
 * Small inline badge showing the source of a field value.
 * Shows a tooltip with the derivation string on hover.
 */
export function SourceBadge({
  source,
  derivation,
  confidence,
}: SourceBadgeProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const config = SOURCE_CONFIG[source];

  if (!config) return null;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: tooltip trigger
    <span
      role="presentation"
      className="relative inline-flex"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span
        className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none cursor-help ${config.className}`}
      >
        <BadgeIcon type={config.icon} />
        {config.label}
      </span>
      {showTooltip && derivation && (
        <span className="absolute bottom-full right-0 mb-1.5 z-50 w-56 rounded-lg bg-gray-900 px-3 py-2 text-xs text-gray-200 shadow-lg pointer-events-none">
          <span className="block font-medium text-white mb-0.5">
            {config.label}
          </span>
          <span className="block leading-relaxed">{derivation}</span>
          {confidence && (
            <span className="block mt-1 text-dim-foreground">
              Confidence: {confidence}
            </span>
          )}
          <span className="absolute top-full right-4 -mt-px border-4 border-transparent border-t-gray-900" />
        </span>
      )}
    </span>
  );
}
